import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { FileSignature, Copy, Download, Link2, Ban, RotateCcw, Eye, ClipboardList, Mail } from 'lucide-react';

const STATUS_META = {
  awaiting_signature: { label: 'AWAITING SIGNATURE', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  signed:             { label: 'SIGNED',             cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  not_required:       { label: 'NOT REQUIRED',       cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  overridden_not_required: { label: 'MARKED NOT REQUIRED', cls: 'bg-slate-200 text-slate-700 border-slate-400' },
  overridden_paper:   { label: 'PAPER SIGNATURE ON FILE', cls: 'bg-blue-100 text-blue-800 border-blue-300' },
};

export default function AgreementSection({ orderId, isAdmin }) {
  const { token, API } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [state, setState] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState('');
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideKind, setOverrideKind] = useState('not_required');
  const [overrideReason, setOverrideReason] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/agreements/order/${orderId}`, auth),
        axios.get(`${API}/agreements/order/${orderId}/audit`, auth).catch(() => ({ data: [] })),
      ]);
      setState(s.data);
      setAudit(a.data || []);
    } catch (e) {
      // A 404 order or 403 just hides the card — nothing to show
      setState(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [API, orderId, token]);

  useEffect(() => { load(); }, [load]);

  if (loading || !state) return null;

  // Hide the whole card if agreements are turned off AND there's no override history to show.
  if (state.status === 'not_required' && audit.length === 0) return null;

  const meta = STATUS_META[state.status] || STATUS_META.not_required;

  const createLink = async () => {
    try {
      const { data } = await axios.post(`${API}/agreements/order/${orderId}/create-link`, {}, auth);
      const url = `${window.location.origin}/agreements/sign/${data.token}`;
      setShowLink(url);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create signing link');
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Link copied'); }
    catch { toast.error('Copy failed — please select and copy manually'); }
  };

  const submitOverride = async () => {
    try {
      await axios.post(`${API}/agreements/order/${orderId}/override`,
        { kind: overrideKind, reason: overrideReason }, auth);
      setOverrideOpen(false);
      setOverrideReason('');
      await load();
      toast.success('Agreement override applied');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save override');
    }
  };

  const clearOverride = async () => {
    if (!window.confirm('Clear the current override for this order?')) return;
    try {
      await axios.post(`${API}/agreements/order/${orderId}/override`,
        { kind: 'clear', reason: '' }, auth);
      await load();
      toast.success('Override cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear override');
    }
  };

  const downloadPdf = async (signedId) => {
    try {
      const res = await axios.get(`${API}/agreements/signed/${signedId}/pdf`,
        { ...auth, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `agreement-${signedId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not download PDF'); }
  };

  const voidSigned = async (signedId) => {
    const reason = window.prompt('Reason for voiding this signed agreement?');
    if (reason === null) return;
    try {
      await axios.post(`${API}/agreements/signed/${signedId}/void?reason=${encodeURIComponent(reason)}`, {}, auth);
      await load();
      toast.success('Signed agreement voided');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    }
  };

  const sendEmail = async () => {
    setEmailSending(true);
    try {
      const { data } = await axios.post(`${API}/agreements/order/${orderId}/send-email`,
        { to_email: emailTo || null, message: emailMessage || null,
          origin: window.location.origin }, auth);
      if (data.sent) {
        toast.success(`Signing link emailed to ${data.to_email}`);
      } else {
        toast.warning(`Email send failed — link created but not delivered. ${data.error || ''}`);
      }
      setEmailOpen(false);
      setEmailMessage('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send email');
    } finally {
      setEmailSending(false);
    }
  };

  const openEmailDialog = () => {
    setEmailTo('');  // will fall back to order.client_email on server
    setEmailOpen(true);
  };

  return (
    <Card className="border-0 shadow-lg mb-4" data-testid="agreement-section">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-slate-700" />
            Customer Agreement
          </CardTitle>
          <Badge className={`border ${meta.cls}`} data-testid="agreement-status-badge">{meta.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* AWAITING SIGNATURE */}
        {state.status === 'awaiting_signature' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This order requires a signed customer agreement before it can progress to the deposit stage.
            </p>
            {state.pending_link ? (
              <PendingLink token={state.pending_link.token}
                           onRefresh={createLink}
                           onCopy={copy}
                           onEmail={openEmailDialog} />
            ) : (
              <div className="flex gap-2 flex-wrap">
                <Button onClick={createLink} data-testid="create-signing-link"
                        className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Link2 className="w-4 h-4 mr-2" /> Create Signing Link
                </Button>
                <Button onClick={openEmailDialog} data-testid="email-signing-link"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  <Mail className="w-4 h-4 mr-2" /> Email to Customer
                </Button>
              </div>
            )}
            {isAdmin && <OverrideRow onOpen={() => setOverrideOpen(true)} />}
          </div>
        )}

        {/* SIGNED */}
        {state.status === 'signed' && state.signed && (
          <div className="space-y-3">
            <div className="p-3 rounded border bg-emerald-50 border-emerald-200 text-sm text-emerald-900">
              Signed by <strong>{state.signed.signer_name}</strong> on{' '}
              {new Date(state.signed.signed_at).toLocaleString('en-GB')} ({state.signed.signature_type})
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => downloadPdf(state.signed.id)}
                      data-testid="download-agreement-pdf">
                <Download className="w-4 h-4 mr-2" /> Download PDF
              </Button>
              {isAdmin && (
                <Button variant="outline" className="text-red-600 hover:bg-red-50 border-red-200"
                        onClick={() => voidSigned(state.signed.id)} data-testid="void-signed">
                  <Ban className="w-4 h-4 mr-2" /> Void
                </Button>
              )}
            </div>
          </div>
        )}

        {/* OVERRIDES */}
        {(state.status === 'overridden_not_required' || state.status === 'overridden_paper') && (
          <div className="space-y-3">
            <div className="p-3 rounded border bg-slate-50 border-slate-200 text-sm text-slate-700">
              {state.status === 'overridden_not_required'
                ? 'This order has been manually marked as not requiring a customer agreement.'
                : 'A paper signature has been recorded against this order.'}
              {state.override?.reason && <><br /><span className="text-slate-500">Reason: {state.override.reason}</span></>}
              {state.override?.by && <div className="text-xs text-slate-500 mt-1">By {state.override.by} · {new Date(state.override.at).toLocaleString('en-GB')}</div>}
            </div>
            {isAdmin && (
              <Button variant="outline" onClick={clearOverride} data-testid="clear-override">
                <RotateCcw className="w-4 h-4 mr-2" /> Clear Override
              </Button>
            )}
          </div>
        )}

        {/* Freshly created link surface */}
        {showLink && (
          <div className="p-3 rounded bg-slate-900 text-slate-100 text-xs break-all"
               data-testid="new-signing-link">
            <div className="flex items-center justify-between gap-2 mb-1">
              <strong>Signing link (send to customer):</strong>
              <Button size="sm" variant="secondary" onClick={() => copy(showLink)}>
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <div>{showLink}</div>
          </div>
        )}

        {/* Audit trail */}
        {audit.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500 flex items-center gap-1">
              <ClipboardList className="w-3 h-3" /> Audit log ({audit.length})
            </summary>
            <ul className="mt-2 space-y-1 pl-4 list-disc text-slate-600">
              {audit.map(a => (
                <li key={a.id}>
                  <span className="font-mono text-[11px]">{new Date(a.at).toLocaleString('en-GB')}</span>{' '}
                  · <strong>{a.action}</strong>
                  {a.by && <> · by {a.by}</>}
                  {a.reason && <> · {a.reason}</>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </CardContent>

      {/* Email to customer modal */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Email signing link to customer</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-1">Customer email (leave blank to use the address on the order)</div>
              <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                     placeholder="Uses order's client email if blank"
                     className="w-full px-3 py-2 border rounded"
                     data-testid="email-to-input" />
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Personal note (optional)</div>
              <Textarea rows={3} value={emailMessage} onChange={e => setEmailMessage(e.target.value)}
                        placeholder="e.g. Please sign this ahead of your fitting on Friday. Thanks!"
                        data-testid="email-message-input" />
            </div>
            <p className="text-[11px] text-slate-500">
              A new signing link will be generated and sent using your company's SMTP settings. Any previous
              unused link for this order will be invalidated.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="submit-email">
              <Mail className="w-4 h-4 mr-2" />
              {emailSending ? 'Sending…' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override modal */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Override customer agreement</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <label className="flex items-center gap-2">
              <input type="radio" checked={overrideKind === 'not_required'}
                     onChange={() => setOverrideKind('not_required')} />
              Mark as <strong>not required</strong> for this order
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" checked={overrideKind === 'paper'}
                     onChange={() => setOverrideKind('paper')} />
              Record a <strong>paper signature</strong> on file
            </label>
            <div>
              <div className="text-xs text-slate-500 mb-1">Reason (recorded in the audit log)</div>
              <Textarea rows={3} value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                        data-testid="override-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverrideOpen(false)}>Cancel</Button>
            <Button onClick={submitOverride} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-override">
              Save Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PendingLink({ token, onRefresh, onCopy, onEmail }) {
  const url = `${window.location.origin}/agreements/sign/${token}`;
  return (
    <div className="p-3 rounded border bg-amber-50 border-amber-200">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <strong className="text-sm text-amber-900">Awaiting customer signature</strong>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={onEmail}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="email-pending-link">
            <Mail className="w-3 h-3 mr-1" /> Email to Customer
          </Button>
          <Button size="sm" variant="outline" onClick={() => onCopy(url)} data-testid="copy-pending-link">
            <Copy className="w-3 h-3 mr-1" /> Copy Link
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.open(url, '_blank')} data-testid="open-pending-link">
            <Eye className="w-3 h-3 mr-1" /> Open
          </Button>
          <Button size="sm" variant="ghost" onClick={onRefresh} data-testid="regenerate-link">
            <RotateCcw className="w-3 h-3 mr-1" /> New
          </Button>
        </div>
      </div>
      <div className="text-[11px] font-mono break-all text-amber-900/80">{url}</div>
    </div>
  );
}

function OverrideRow({ onOpen }) {
  return (
    <div className="pt-2 border-t border-dashed">
      <button type="button" onClick={onOpen}
              className="text-xs text-slate-500 hover:text-slate-800 underline"
              data-testid="open-override-dialog">
        Admin override (mark not required / record paper signature)
      </button>
    </div>
  );
}
